import { Component, OnInit, ViewChild, ElementRef, Input, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import * as d3 from 'd3';

@Component({
    selector: 'app-graph',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './graph.component.html',
    styleUrls: ['./graph.component.css'],
    standalone: false
})
export class GraphComponent implements OnInit {

  @Input() xData: number[];
  @Input() yData: any;
  @Input() wideGraph: boolean = false;
  @Input() scale: number[] = null;
  @Input() yAxisName: string;
  @Input() frequencyDividers: {frequency: any, iteration: any}[];
  @Output('point') point: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild('graph', { static: true }) graph: ElementRef;
  @ViewChild('graphContainer', { static: true }) container: ElementRef;

  xValue: number = 0;
  yValue: number = 0;
  showBlockLabels: boolean = false
  showGridLines: boolean = false;
  svg: any
  xScale: any
  yScale: any  

  private width: number;
  private height: number;
  private margin = {top: 0.1, right: 0.1, bottom: 0.1, left: 0.15};

  constructor() { }

  ngOnInit() { }

  ngAfterViewInit() {
    this.width = this.container.nativeElement.offsetWidth*0.8;
    if (this.wideGraph){
      this.height = this.width*0.375;
      this.margin.left = 0.075;
    }
    else {
      this.height = this.width*0.75;
    }

    let xData = this.xData;
    let yData = this.yData.data.filter(d => d !== null);

    let xRange = Math.max(...xData)-Math.min(...xData);
    let minX = Math.min(...this.xData)-0.05*xRange;
    let maxX = Math.max(...this.xData)+0.05*xRange;

    let minY = Math.min(...yData)
    let maxY = Math.max(...yData)
    if (minY == maxY) {
      minY *= 0.9;
      maxY *= 1.1;
    }
    if (this.scale[0] != null) {
      minY = this.scale[0];
    }
    if (this.scale[1] != null && this.scale[1] > minY) {
      maxY = this.scale[1];
    }
    let yRange = maxY - minY;
    minY = minY-0.05*yRange;
    maxY =  maxY+0.05*yRange;

    this.draw(minX, maxX, minY, maxY, xData, this.yData.data);
  }

  toggleBlockLabels(){
    this.showBlockLabels = !this.showBlockLabels
    if (this.showBlockLabels){
      // Frequency divider lines
      let filteredFrequencyDividers = []
      if (this.xData.length == 1){
        filteredFrequencyDividers = [this.frequencyDividers[0]];
      }else{
        filteredFrequencyDividers = this.frequencyDividers;
      }
      const g = this.svg.append("g").attr('class', 'block-labels-container').attr("clip-path", "url(#rect_clip)");
      g.selectAll(".vertical-line")
        .data(filteredFrequencyDividers)
        .enter()
        .append("line")
        .attr("class", "vertical-line")
        .attr("x1", d => this.xScale(d.iteration))
        .attr("y1", 0) // Start at the top of the inner height
        .attr("x2", d => this.xScale(d.iteration))
        .attr("y2", this.height) // End at the bottom of the inner height
        .style("stroke", "#0000FF") // Blue color
        .style("stroke-width", 1)
        .style("stroke-opacity", 0.5) 
        .style("stroke-dasharray", "3,3"); // Optional: add dashed style
      g.selectAll(".vertical-line-label")
        .data(filteredFrequencyDividers)
        .enter()
        .append("text")
        .attr("class", "vertical-line-label")
        .attr("x", d => this.xScale((d.iteration - (d.width/2)+ 0.5)))
        .attr("y", 10) // Adjust vertical position above the line
        .attr("text-anchor", "middle") // Center the text horizontally
        .style("font-size", "10px")
        .text(d => `${d.frequency} Hz`); // Customize the label text
      g.selectAll(".vertical-line-functional-label")
        .data(filteredFrequencyDividers)
        .enter()
        .append("text")
        .attr("class", "vertical-line-label")
        .attr("x", d => this.xScale((d.iteration - (d.width/2))+ 0.5))
        .attr("y", 20) // Adjust vertical position above the line
        .attr("text-anchor", "middle") // Center the text horizontally
        .style("font-size", "10px")
        .text(d => `${d.functional}`); // Customize the label text
    }else{
      if (this.svg) {
        this.svg.selectAll('.block-labels-container').remove();
      }
    }
  }

  toggleGridLines(){
    this.showGridLines = !this.showGridLines;
    if (this.showGridLines){
      
      const gridContainer = this.svg.insert("g", ":first-child")
        .attr('class', 'grid-lines-container')
        .attr("clip-path", "url(#rect_clip)");
      
      // X-axis grid lines (vertical lines)
      const xGridAxis = d3.axisBottom(this.xScale)
      .tickValues(this.xScale.ticks().filter(tick => Number.isInteger(tick)))
        .tickSize(-this.height)
        .tickFormat(null); // Remove tick labels
      
      gridContainer.append("g")
        .attr("class", "x-grid")
        .attr("transform", `translate(0,${this.height})`)
        .call(xGridAxis)
        .selectAll("line")
        .style("opacity", 0.3)
        .style("stroke-dasharray", "3,3");
      
      // Y-axis grid lines (horizontal lines)
      const yGridAxis = d3.axisLeft(this.yScale)
        .tickSize(-this.width)
        .tickFormat(null); // Remove tick labels
      
      gridContainer.append("g")
        .attr("class", "y-grid")
        .call(yGridAxis)
        .selectAll("line")
        .style("opacity", 0.3)
        .style("stroke-dasharray", "3,3");
      
      // Remove the domain lines (the main axis lines)
      gridContainer.selectAll(".domain").remove();
    }else{
      if (this.svg) {
        this.svg.selectAll('.grid-lines-container').remove();
      }
    }
  }

  private draw(minX: number, maxX: number, minY: number, maxY: number,
    xData: number[], yData: number[]) {
    let plotData: number[][] = xData.map((e, i) => [e, yData[i]]).filter(d => d[1] !== null && d[1] !== undefined);
    plotData.sort((a, b) => a[0]-b[0])
    this.xScale = d3.scaleLinear()
                   .domain([minX, maxX])
                   .range([0, this.width]);
    this.yScale = d3.scaleLinear()
                   .domain([minY, maxY])
                   .range([this.height, 0]);
                   

    let line = d3.line().x((d: number[]) => {return this.xScale(d[0]);})
                        .y((d: number[]) => {return this.yScale(d[1]);});
    this.svg = d3.select(this.graph.nativeElement)
      .attr("width", this.width*(1+this.margin.left+this.margin.right))
      .attr("height", this.height*(1+this.margin.top+this.margin.bottom))
      .append("g")
      .attr("transform", 'translate('+this.width*this.margin.left+', '+this.height*this.margin.top+')');
    
    this.toggleGridLines();
    this.toggleBlockLabels()

    const xAxis = d3.axisBottom(this.xScale)
    .tickValues(this.xScale.ticks().filter(tick => Number.isInteger(tick)))
    .tickFormat(d3.format('d'));

    this.svg.append("g")
    .attr("transform", 'translate(0,' + this.height + ')')
    .call(xAxis);


    this.svg.append("text").attr("transform", 'translate('+ (this.width/2) +','+(this.height+40)+')')
       .text("Iteration").style("font-weight", "bold")
    this.svg.append("g").call(d3.axisLeft(this.yScale).tickFormat(d3.format(".2e"))); // makes the numbers 2 decimal place and the 'e' makes them scientific notation
    this.svg.append("text").attr("transform", 'translate(-60,'+ this.height/2 +') rotate(-90)')
       .text(() => this.yAxisName).style("font-weight", "bold")
    
       // Add path
    this.svg.append("g").selectAll("path").data([plotData]).enter().append("path").attr("class", "fline").attr("d",line).attr("stroke", "black")
      .attr("stroke-width", 1.5).attr("clip-path", "url(#rect_clip)").style("fill", "none");
    // Add nodes
    this.svg.append("g").selectAll("circle").data(plotData).enter().append("circle")
       .attr("cx", (d: number[]) => {return this.xScale(d[0])})
       .attr("cy", (d: number[]) => {;return this.yScale(d[1])}).attr("r", 5)
       .on("mouseover", function(){d3.select(this).attr("r", 8).style("opacity", 0.7)})
       .on("mouseout", function(){d3.select(this).attr("r", 5).style("opacity", 1)})
       .on("click",(event, d: number[]) => {this.point.emit(xData.indexOf(d[0]))})
       .attr("clip-path", "url(#rect_clip");
    this.svg.append("clipPath").attr("id", "rect_clip").append("rect").attr("x", 0).attr("y", 0).attr("height", this.height).attr("width", this.width);

  }
}